import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { BookingForm } from "@/components/BookingForm";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Calendar } from "lucide-react";

export default function BookAppointment() {
  const [services, setServices] = useState<Array<{ id: string; name: string }>>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch services
        const { data: servicesData, error: servicesError } = await supabase
          .from("services")
          .select("id, name")
          .order("name");

        if (servicesError) throw servicesError;
        setServices(servicesData || []);

        // Fetch cities from city_groups
        const { data: cityGroupsData, error: cityGroupsError } = await supabase
          .from("city_groups")
          .select("display_cities");

        if (cityGroupsError) throw cityGroupsError;

        const allCities = cityGroupsData
          ?.flatMap((group) => group.display_cities)
          .filter((city, index, self) => self.indexOf(city) === index)
          .sort() || [];

        setCities(allCities);
      } catch (error: any) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load booking form data");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return (
    <main className="min-h-screen">
      <SEOHead
        title="Book an Appointment | Safeway Roofing & Chimney"
        description="Schedule your free estimate or service appointment with Safeway Roofing & Chimney. Expert roofing services in Suffolk and Nassau County, Long Island."
        canonical="https://quote.safewayroofingandchimney.com/book-appointment"
      />
      <Header />
      
      <section className="py-12 md:py-20">
        <div className="container max-w-3xl">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-primary/10 p-4 rounded-full">
                <Calendar className="w-8 h-8 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Book Your Appointment
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Schedule a free estimate or service appointment with our expert team. 
              We serve all of Suffolk and Nassau County, Long Island.
            </p>
          </div>

          <div className="bg-card border rounded-lg shadow-lg p-6 md:p-8">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : (
              <BookingForm services={services} cities={cities} />
            )}
          </div>

          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p>
              Need immediate assistance? Call us at{" "}
              <a href="tel:+15165551234" className="text-primary hover:underline font-semibold">
                (516) 555-1234
              </a>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
